/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react'
import axios, { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  Grid,
  IconButton,
  Box,
  CircularProgress,
  Button
} from '@material-ui/core'
import { CloudDownload, NotInterested } from '@material-ui/icons'
import { ServerError } from '../../../schemas/Error'
import UserContext from '../../../contexts/User'
import { UserContextType } from '../../../schemas/User'
import PageContainer from '../Common/PageContainer'
import ResponsiveList, { ResponsiveColumn } from '../Common/ResponsiveList'
import DocumentStateBadge from '../Common/DocumentStateBadge'
import SupportDocumentService from '../../../services/SupportDocument'
import Utils from '../../../utils'
import permissions from '../../../permissions.json'
import PresentCreditNoteSupportDocumentModal from './PresentCreditNoteSupportDocumentModal'

// -------------- Styles --------------
const useStyles = makeStyles((theme: Theme) => ({
  alignRight: {
    textAlign: 'right',
  },
  loadMoreButton: {
    minHeight: 44,
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  loadingContainer: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    bottom: '0',
    right: '0',
    background: '#BFBFBFBF',
    opacity: 0.6,
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '50vh'
  }
}))

// - in this part all the component texts are housed
const texts = {
  header: {
    description: 'Lista de documentos emitidos'
  },
  body: {
    buttonLoadMore: {
      label: 'Cargar más ...'
    }
  }
}

// widthPct: porcentaje del ancho de la tabla (las columnas visibles siempre suman 100%)
const columns: ResponsiveColumn[] = [
  {
    field: 'number',
    headerName: 'Documento',
    widthPct: 12,
    editable: false,
  },
  {
    field: 'date',
    headerName: 'Fecha',
    widthPct: 15,
    editable: false,
  },
  {
    field: 'third',
    headerName: 'Nombre',
    editable: false,
    widthPct: 22,
    cardTitle: true,
  },
  {
    field: 'note',
    headerName: 'Descripción',
    editable: false,
    widthPct: 17,
    hideOnTablet: true,
  },
  {
    field: 'state',
    headerName: 'Estado',
    editable: false,
    widthPct: 10,
    renderCell: (params: any) => (
      <DocumentStateBadge state={params.row.state} />
    ),
    renderCard: (row: any) => <DocumentStateBadge state={row.state} />,
  },
  {
    field: 'actions',
    headerName: 'Acciones',
    widthPct: 24,
    minWidth: 140,
    type: 'actions',
    renderCell: (params: any) => (
      <ActionButtons params={params} />
    )
  }
]

// ------------ Init state -----------
interface State {
  documents: any[]
  loading: boolean
}

const initState: State = {
  documents: [],
  loading: true,
}

function ActionButtons(props: any) {
  const { userContext } = React.useContext(UserContext) as UserContextType
  const [showConfirmCreditNoteModal, setShowConfirmCreditNoteModal] = useState(false)
  const [cudeForCreditNote, setCudeForCreditNote] = useState<string | null>(null)
  const [documentNumberForCreditNote, setDocumentNumberForCreditNote] = useState<string | null>(null)

  const handleConfirmCreditNoteModal = (show: boolean) => {
    setShowConfirmCreditNoteModal(show)
    if (!show) {
      setCudeForCreditNote(null)
      setDocumentNumberForCreditNote(null)
    }
  }

  const onOpenPresentCreditNoteModal = () => {
    if (props.params.row.preview != null) {
      setCudeForCreditNote(props.params.row.preview)
      setDocumentNumberForCreditNote(props.params.row.number)
      setShowConfirmCreditNoteModal(true)
    } else {
      toast.error('No hay CUDE disponible para presentar nota crédito de documento soporte')
    }
  }

  const onDownload = () => {
    if (props.params.row.qrStr !== undefined) {
      const regex = /https?:\/\/[^\s]+/g;
      const matches = props.params.row.qrStr.match(regex)
      let url = matches ? matches[0] : undefined
      return window.open(url, '_blank')
    } else {
      return toast.warn('Documento disponible ingresando a la visualización')
    }
  }

  const onDownloadPDF = async () => {
    if (props.params.row.preview !== undefined) {
      try {
        const supportDocumentService = new SupportDocumentService()
        const htmlContent = await supportDocumentService.getSupportDocumentHTML(props.params.row.preview)
        
        // Generar el PDF con el HTML obtenido
        const documentNumber = props.params.row.number
        Utils.generateSupportDocumentPDF(htmlContent, documentNumber)
        
        // Mostrar mensaje de éxito
        toast.success('PDF de documento soporte generado correctamente')
      } catch (error) {
        console.error('Error generando PDF de documento soporte:', error)
        toast.error(`Error generando PDF de documento soporte: ${error}`)
      }
    } else {
      toast.error('No hay documento soporte disponible')
    }
  }

  return (
    <React.Fragment>
      <IconButton
        type="button"
        onClick={() => onDownloadPDF()}
        title="Descargar documento soporte"
      >
        <CloudDownload fontSize={"small"} color="primary" />
      </IconButton>
      <IconButton
        type="button"
        onClick={() => onDownload()}
        title="Descargar documento soporte desde la DIAN"
      >
        <CloudDownload fontSize={"small"} color="action" />
      </IconButton>

      {userContext.permissions.some(permission => permission === permissions.support_document.present_credit_note) && (
        <IconButton
          type="button"
          title="Presentar nota crédito"
          onClick={onOpenPresentCreditNoteModal}
        >
          <NotInterested fontSize="small" color="secondary" />
        </IconButton>
      )}
      <PresentCreditNoteSupportDocumentModal
        show={showConfirmCreditNoteModal}
        cude={cudeForCreditNote}
        documentNumber={documentNumberForCreditNote}
        handleModal={handleConfirmCreditNoteModal}
      />
    </React.Fragment>
  )
}

export default function SupportDocumentList() {

  const classes = useStyles()

  const [state, setState] = useState<State>(initState)
  const [page, setPage] = useState<number>(1)

  React.useEffect(() => {
    async function loadData() {
      try {
        setState({
          ...state,
          loading: true
        })
        const supportDocumentService= new SupportDocumentService()
        const response = await supportDocumentService.list(page)
        let documents = [];

        if (response.totalDocuments > 0) {
          documents = response.docs.map((document: any) => {
            return {
              number: `${document.prefix}${document.number}`,
              date: document.date,
              third: document.seller.name,
              note: document.head_note,
              state: document.state,
              preview: document.cude,
              qrStr: document.QRStr
            }
          })
        }
        setState({
          ...state,
          documents: documents,
          loading: false
        })
      } catch (error) {
        setState({
          ...state,
          loading: false
        })
        if (axios.isAxiosError(error)) {
          const serverError = error as AxiosError<ServerError>
          if (serverError && serverError.response) {
            return toast.error(serverError.response.data.message)
          }
        }
      }
    }
    loadData()
  }, [page])

  return (
    <PageContainer title={texts.header.description}>
      <Grid container spacing={3}>

        {/* ----- Listado: tabla (>= 600px) o cards (mobile) ------*/}
        <Grid item xs={12}>
          <ResponsiveList
            rows={state.documents}
            columns={columns}
            keyId='number'
            cardActions={(row) => <ActionButtons params={{ row }} />}
          />
        </Grid>

        <Grid item xs={12} className={classes.alignRight}>
          <Button
            type="button"
            color="primary"
            className={classes.loadMoreButton}
            startIcon={<CloudDownload />}
            onClick={() => setPage(page + 1)}
          >
            {texts.body.buttonLoadMore.label}
          </Button>
        </Grid>
      </Grid>
      {
        state.loading ?
        (
          <div className={classes.loadingContainer}>
            <Box>
              <CircularProgress/>
            </Box>
          </div>
        ) : ''
      }
    </PageContainer>
  )
}
